/**
 * Manifest validation. A manifest that breaks any rule below is a defect that
 * must stop the build, not a runtime surprise — so `assertManifestValid()` runs
 * at module import (see ./authz.ts), again at API startup, and in CI.
 *
 * The rules, verbatim from the specification:
 *
 *   full ⊇ edit ⊇ view ⊇ none
 *   special_actions ⊆ actions
 *   special_actions ∩ (view ∪ edit ∪ full) = ∅
 *   actions = (view ∪ edit ∪ full) ∪ special_actions
 *
 * plus the structural rules that make the above meaningful: unique module keys,
 * globally unique action keys, every action carrying its module's namespace, a
 * `requires_level` that names a level the module defines, and `none` empty.
 *
 * Why `actions = levels ∪ specials` matters more than it looks: it is what stops
 * `full` from quietly becoming "everything". A new action must be placed in a
 * level or declared special, or the manifest does not load.
 */
import type { ModuleDefinition } from "@yazamco/shared-types";

export class ManifestError extends Error {
  constructor(public readonly problems: string[]) {
    super(
      `Invalid module manifest:\n${problems.map((p) => `  - ${p}`).join("\n")}`,
    );
    this.name = "ManifestError";
  }
}

const LEVELS = ["view", "edit", "full"] as const;

function isSuperset(outer: readonly string[], inner: readonly string[]): boolean {
  const set = new Set(outer);
  return inner.every((x) => set.has(x));
}

/**
 * Collects EVERY problem rather than throwing on the first, so a reviewer fixing
 * a manifest sees the whole picture in one run.
 */
export function validateManifest(
  manifest: readonly ModuleDefinition[],
): string[] {
  const problems: string[] = [];
  const seenModules = new Set<string>();
  const actionOwner = new Map<string, string>();

  if (manifest.length === 0) problems.push("manifest is empty");

  for (const m of manifest) {
    const where = `module '${m.key}'`;

    if (seenModules.has(m.key)) problems.push(`duplicate module key '${m.key}'`);
    seenModules.add(m.key);

    if (!/^[a-z][a-z0-9_]*$/.test(m.key)) {
      problems.push(`${where}: key must be lower_snake_case`);
    }
    if (!m.display_name.trim()) problems.push(`${where}: display_name is empty`);
    if (!m.category.trim()) problems.push(`${where}: category is empty`);
    if (!m.description.trim()) problems.push(`${where}: description is empty`);

    // ── namespace ───────────────────────────────────────────────────────────
    const ns = m.action_namespace ?? m.key;
    for (const a of m.actions) {
      if (!a.startsWith(`${ns}.`)) {
        problems.push(
          `${where}: action '${a}' does not carry the '${ns}.' namespace`,
        );
      }
      const owner = actionOwner.get(a);
      if (owner && owner !== m.key) {
        problems.push(
          `action '${a}' is declared by both '${owner}' and '${m.key}'`,
        );
      }
      actionOwner.set(a, m.key);
    }
    if (new Set(m.actions).size !== m.actions.length) {
      problems.push(`${where}: duplicate entries in actions`);
    }

    // ── levels ──────────────────────────────────────────────────────────────
    const { none, view, edit, full } = m.access_levels;
    if (none.length !== 0) problems.push(`${where}: 'none' must be empty`);

    for (const level of LEVELS) {
      const actions = m.access_levels[level];
      if (!Array.isArray(actions)) {
        problems.push(`${where}: level '${level}' is not defined`);
        continue;
      }
      if (new Set(actions).size !== actions.length) {
        problems.push(`${where}: duplicate entries in level '${level}'`);
      }
      for (const a of actions) {
        if (!m.actions.includes(a)) {
          problems.push(
            `${where}: level '${level}' references unknown action '${a}'`,
          );
        }
      }
    }

    // Hierarchy: full ⊇ edit ⊇ view. Equality is legitimate — a module with
    // nothing further to offer repeats the level below it.
    if (!isSuperset(edit, view)) {
      problems.push(
        `${where}: edit is not a superset of view (missing ${view
          .filter((a) => !edit.includes(a))
          .join(", ")})`,
      );
    }
    if (!isSuperset(full, edit)) {
      problems.push(
        `${where}: full is not a superset of edit (missing ${edit
          .filter((a) => !full.includes(a))
          .join(", ")})`,
      );
    }

    // ── special actions ─────────────────────────────────────────────────────
    const levelUnion = new Set<string>([...view, ...edit, ...full]);
    const specialKeys = m.special_actions.map((s) => s.key);
    if (new Set(specialKeys).size !== specialKeys.length) {
      problems.push(`${where}: duplicate special_actions`);
    }

    for (const s of m.special_actions) {
      if (!m.actions.includes(s.key)) {
        problems.push(
          `${where}: special action '${s.key}' is not declared in actions`,
        );
      }
      if (levelUnion.has(s.key)) {
        problems.push(
          `${where}: special action '${s.key}' also appears in an access level — ` +
            `special actions are never implied by a level`,
        );
      }
      if (!LEVELS.includes(s.requires_level)) {
        problems.push(
          `${where}: special action '${s.key}' has requires_level ` +
            `'${s.requires_level}', which is not a level the module defines`,
        );
      }
      if (!s.label.trim() || !s.description.trim()) {
        problems.push(`${where}: special action '${s.key}' needs a label and description`);
      }
    }

    // ── completeness: no orphan, no phantom ─────────────────────────────────
    const covered = new Set<string>([...levelUnion, ...specialKeys]);
    for (const a of m.actions) {
      if (!covered.has(a)) {
        problems.push(
          `${where}: action '${a}' is unreachable — assign it to an access ` +
            `level or declare it a special action`,
        );
      }
    }
    for (const a of covered) {
      if (!m.actions.includes(a)) {
        problems.push(`${where}: '${a}' is referenced but not declared in actions`);
      }
    }
  }

  return problems;
}

/** Throws {@link ManifestError} when the manifest breaks any rule. */
export function assertManifestValid(
  manifest: readonly ModuleDefinition[],
): void {
  const problems = validateManifest(manifest);
  if (problems.length > 0) throw new ManifestError(problems);
}
